import ManagerRepository from 'coreClasses/ManagerRepository';
import VariantPriceModel from './Variant/VariantPriceModel';
import AbstractProductModel from './AbstractProductModel';
import {IProductPriceObject} from './IProduct';
import {
	Product as ProductType,
	ТVariationGroupsColorGroup,
	TieredPrices
} from '../../store/data/products/types';

/**
 * Represents Full Variant Model
 */
class VariantModel extends AbstractProductModel {
	managerRepository: ManagerRepository;
	product: ProductType;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductType} product - variant
	 */
	constructor(managerRepository: ManagerRepository, product: ProductType) {
		super(managerRepository, product);

		this.managerRepository = managerRepository;
		this.product = product;
	}

	/**
	 * Checks if the product is variant
	 * @returns {Boolean} is variant
	 */
	isVariant() {
		return true;
	}

	/**
	 * Returns the product promotions for the current variant
	 * @return {Array} an array containing the Product Promotions
	 */
	getProductPromotions() {
		return this.product?.productPromotions || [];
	}

	/**
	 * Returns price model for the product.
	 */
	get priceModel() {
		return new VariantPriceModel(this);
	}

	/**
	 * Returns the price model for the product.
	 * @return {VariantPriceModel} the price model
	 */
	getPriceModel() {
		return this.priceModel;
	}

	/**
	 * Returns the base price from the Variant Product
	 * @return {IProductPriceObject} the price
	 */
	public getPrice(): IProductPriceObject {
		if (this.product?.tieredPrices?.length) {
			const minPrice = this.product?.tieredPrices.reduce((minPrice: number, tieredPrice) => {
				return (!minPrice || tieredPrice.price < minPrice) ? tieredPrice.price : minPrice;
			}, 0);

			return {
				currency: this.product?.currency,
				value: minPrice,
				pricebook: this.product?.tieredPrices
					.find((tieredPrice) => tieredPrice.price === minPrice).pricebook
			};
		}

		return {
			value: this.product?.price,
			currency: this.product?.currency,
			pricebook: 'default'
		};
	}

	/**
	 * Returns the variant tiered prices
	 * @returns {Array<TieredPrices>} tiered prices
	 */
	public getTieredPrices(): TieredPrices[] {
		return this.product?.tieredPrices;
	}

	/**
	 * Returns variation value
	 * @param {String} variationName - variation name
	 */
	getVariationValue(variationName): string {
		if (this.getVariationValues()) {
			return this.getVariationValues()[variationName] || '-';
		}

		return '-';
	}

	/**
	 * Returns orderable status for the product.
	 * @returns {Boolean} True if the product is orderable
	 */
	isOrderable(): boolean {
		return this.product?.inventory?.orderable || this.product?.orderable;
	}

	/**
	 * Returns preorderable status for the product.
	 * @returns {Boolean} True if the product is preorderable
	 */
	isPreorder(): boolean {
		return this.product?.inventory?.preorderable;
	}

	/**
	 * Checks if to show preorder for the product.
	 * @returns {Boolean} True to show preorder for the product
	 */
	isPreOrderShown(): boolean {
		return this.isPreorder() && !this.isOutOfStock() && this.isOutOfStockLevel();
	}

	/**
	 * Returns out of stock status for the current variant.
	 * @returns {Boolean} True if the current variant is out of stock
	 */
	isOutOfStock(): boolean {
		return this.getAtsStockLevel() === 0 || false;
	}

	/**
	 * Returns out of stock level (from inventory, NOT ats) for the current variant.
	 * @returns {Boolean} True if the current variant is out of stock level (from inventory, NOT ats)
	 */
	isOutOfStockLevel(): boolean {
		return this.getStockLevel() === 0 || false;
	}

	/**
	 * Returns the food type status for the product.
	 * @returns {Boolean} True if the product has food type
	 */
	isFoodType(): boolean {
		return this.product?.c_isFood;
	}

	/**
	 * Returns the alcohol type status for the product.
	 * @returns {Boolean} True if the product has alcohol type
	 */
	isAlcoholType(): boolean {
		return this.product?.c_isAlcohol;
	}

	/**
	 * Returns current ATS for variant
	 * @returns {Number}
	 */
	getAtsStockLevel(): number {
		return this.product?.inventory?.ats;
	}

	/**
	 * Returns true if the product has at least one variant with ats > 0
	 * @returns {Boolean}
	 */
	hasAvailableVariant(): boolean {
		return this.product?.c_hasAvailableVariant || true;
	}

	/**
	 * Returns current Stock Level for variant
	 * @returns {Number}
	 */
	getStockLevel(): number {
		return this.product?.inventory?.stockLevel;
	}

	/**
	 * Returns stock level threshold for the variant
	 * @return {Number|null}
	 */
	getStockLevelThreshold(): number | null {
		return this.product?.c_showStockThreshold;
	}

	/**
	 * Returns variant size
	 * @return {String|null}
	 */
	getSize(): string | null {
		return this.product?.c_size;
	}

	/**
	 * Returns variant width
	 * @return {String|null}
	 */
	getWidth(): string | null {
		return this.product?.c_width;
	}

	/**
	 * Returns object with  variation groups ID as a key and first hexadecBasicColor product attribute as a value
	 * @return {Record<string, string>}
	 */
	getVariationGroupsHexColor(): Record<string, string> {
		return this.product?.c_variationGroupsHexColor || {};
	}

	/**
	 * Returns the product type
	 * @returns {String} product type
	 */
	getProductType(): string {
		return this.product?.c_productType;
	}

	/**
	 * Returns the variation groups colorGroup attributes by variation id
	 * @returns {ТVariationGroupsColorGroup} variation groups colorGroups attributes by variation id
	 */
	getVariationGroupsColorGroup(): ТVariationGroupsColorGroup {
		return this.product?.c_variationGroupsColorGroup;
	}

	/**
	* Returns first variant.
	* @returns {VariantModel} first variant
	*/
	getSingleVariant(): VariantModel {
	   return this.product?.fullVariants
		   ? new VariantModel(this.managerRepository, this.product?.fullVariants[0])
		   : new VariantModel(this.managerRepository, {} as ProductType);
	}

	/**
	 * Retrieves the product ID of the first variation group in the product.
	 *
	 * @returns {string | undefined} The product ID of the first variation group, or undefined if not available.
	 */
	getFirstVariationGroupId() {
		return this.product?.variationGroups[0]?.productId;
	}
}

export default VariantModel;
